import * as THREE from "three";

/** The unit uber-shader's GLSL and its `onBeforeCompile` patch (unitFx.ts documents the uniforms; UnitLook applies it). */

/** Uniform declarations and the noise both the lit and the basic patch use. */
const SHARED_GLSL = /* glsl */ `
uniform float uTdFlash;
uniform vec4 uTdRim;
uniform vec4 uTdStatus;
uniform float uTdDissolve;
uniform float uTdCrack;
uniform float uTdEdgeW;
uniform vec3 uTdEdge;
uniform float uTdH;
uniform float uTdTime;
uniform vec4 uTdShiny;
uniform vec3 uTdShinyA;
uniform vec3 uTdShinyB;
varying vec3 vTdLocal;
float tdHash(vec3 p) { p = fract(p * 0.3183099 + 0.1); p *= 17.0; return fract(p.x * p.y * p.z * (p.x + p.y + p.z)); }
float tdNoise(vec3 x) {
  vec3 i = floor(x); vec3 f = fract(x); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(mix(tdHash(i), tdHash(i + vec3(1.0, 0.0, 0.0)), f.x), mix(tdHash(i + vec3(0.0, 1.0, 0.0)), tdHash(i + vec3(1.0, 1.0, 0.0)), f.x), f.y),
             mix(mix(tdHash(i + vec3(0.0, 0.0, 1.0)), tdHash(i + vec3(1.0, 0.0, 1.0)), f.x), mix(tdHash(i + vec3(0.0, 1.0, 1.0)), tdHash(i + vec3(1.0, 1.0, 1.0)), f.x), f.y), f.z);
}`;

/*
 * The rare spawn's shine (M36, "like a shiny Pokemon"; render/views/rareShiny.ts drives it). uTdShiny = (strength,
 * glitter, glow, hue drift); uTdShinyA/B the rare's two sparkle colours (linear). Three layers, all emissive so the
 * bloom pass catches them, and the sheen and glow scaled by the painted albedo under them (`body`, the lit material's
 * diffuseColor), so the creature keeps its own colours with the sparkle on top (review 2026-10-23: the navy Ram and
 * the dark Salamander read as pale ghosts under an albedo-blind additive film):
 * - iridescent sheen: a thin-film rainbow (cosine palette) whose phase runs with the view angle, the body's own space
 *   and time, mixed with the rare's colours, weakest face-on and strongest at the silhouette (fresnel);
 * - glitter: sparse cells in the model's own space that flash for a frame or two as the view (normal) and time turn —
 *   a few bright white-gold glints at any moment, not a noise field;
 * - glow: the body's own paint breathing brighter, tinted to the rare (the CPU writes the pulse into uTdShiny.z).
 * The branch is on a uniform: every other unit skips it.
 */
const SHINY_GLSL = /* glsl */ `
const float TD_SHINY_FLOOR = 0.2;
const float TD_SHINY_LUM_GAIN = 3.0;
const float TD_SHINY_BREATH = 0.25;
vec3 tdShinyLight(vec3 n, float nv, float f3, vec3 body) {
  float s = uTdShiny.x;
  vec3 lp = vTdLocal / max(uTdH, 0.05);
  float ph = nv * 1.6 + dot(lp, vec3(0.9, 1.7, 0.6)) + uTdShiny.w;
  vec3 iri = 0.5 + 0.5 * cos(6.28318 * (ph + vec3(0.0, 0.33, 0.67)));
  vec3 tint = mix(uTdShinyA, uTdShinyB, 0.5 + 0.5 * sin(ph * 3.1));
  // a saturated film at the silhouette, only a breath of it face-on (the painted body must still read), weighted by
  // the paint under it: added light on a dark hide (the Ram's navy, the Salamander's soot) veiled it pale
  float rim = nv * nv; rim *= rim;
  float lum = dot(body, vec3(0.2126, 0.7152, 0.0722));
  float onBody = TD_SHINY_FLOOR + (1.0 - TD_SHINY_FLOOR) * clamp(lum * TD_SHINY_LUM_GAIN, 0.0, 1.0);
  vec3 c = mix(tint * 0.5, iri, 0.7) * (0.03 + 0.75 * rim) * onBody;
  // glitter: uTdShiny.y cells per body height (rareShiny.ts glitterCells: sized for the screen, so the glints are a few
  // pixels across at the game camera and fine at a close-up; 0 = none). Coarser cells flash more often and each glint
  // is a round spark inside its cell (a whole lit cube read as a square at the far zoom)
  if (uTdShiny.y > 0.0) {
    vec3 gq = lp * uTdShiny.y;
    vec3 gp = floor(gq);
    float gh = tdHash(gp);
    float gt = tdHash(gp + floor(uTdTime * 7.0 + gh * 13.0) + floor(n * 3.0));
    // few and sharp: a haze of glints under the bloom pass turns the body to a pale ghost
    float gk = clamp(0.014 * pow(38.0 / uTdShiny.y, 1.4), 0.014, 0.07);
    float spark = 1.0 - smoothstep(0.12, 0.5, length(fract(gq) - 0.5));
    float glint = step(1.0 - gk, gt) * spark * (0.6 + 0.4 * sin(uTdTime * 31.0 + gh * 50.0));
    c += vec3(1.0, 0.95, 0.82) * (glint * 2.6);
  }
  // the glow breathes in the body's own colours (its paint lifted, tinted to the rare), not a pale wash over them
  c += body * uTdShinyA * (uTdShiny.z * TD_SHINY_BREATH);
  return c * s;
}`;

/*
 * Dissolve, first thing in main(): coarse value noise (about seven blobs per body height) plus fine grain, mixed 35 %
 * toward the height so the feet go first. The threshold runs to 1.1 so that dissolve = 1 removes every fragment. The
 * branch is on a uniform, so the living units (dissolve 0) skip it wholesale.
 */
const DISSOLVE_GLSL = /* glsl */ `
  float tdEdgeGlow = 0.0;
  if (uTdDissolve > 0.0) {
    vec3 tdP = vTdLocal / max(uTdH, 0.05);
    float tdN = tdNoise(tdP * 7.0) * 0.75 + tdHash(floor(tdP * 40.0)) * 0.25;
    tdN = mix(tdN, clamp(tdP.y, 0.0, 1.0), 0.35);
    float tdCut = uTdDissolve * 1.1;
    if (tdN < tdCut) discard;
    tdEdgeGlow = 1.0 - smoothstep(0.0, uTdEdgeW, tdN - tdCut);
  }`;

/*
 * Status tints on the albedo, after color_fragment. The colours are the review's hex values in linear space:
 * #a8d8ff = (0.392, 0.686, 1.0), #7cff5a = (0.202, 1.0, 0.102).
 */
const STATUS_ALBEDO_GLSL = /* glsl */ `
  float tdPulse = 0.5 + 0.5 * sin(uTdTime * 12.566);
  if (uTdStatus.x > 0.0) {
    float tdLum = dot(diffuseColor.rgb, vec3(0.299, 0.587, 0.114));
    vec3 tdFrost = mix(diffuseColor.rgb, vec3(tdLum), 0.5) * vec3(0.392, 0.686, 1.0) * 1.35;
    diffuseColor.rgb = mix(diffuseColor.rgb, tdFrost, uTdStatus.x);
  }
  if (uTdStatus.z > 0.0) diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(0.55, 1.0, 0.4) + vec3(0.03, 0.09, 0.0), uTdStatus.z * (0.25 + 0.2 * tdPulse));`;

/*
 * Emissive terms, after emissivemap_fragment (normal and vViewPosition are in view space here): team rim, frost
 * fresnel, burn flicker weighted to the feet, poison pulse on the rim, gold boost rim.
 * #ff6a1a = (1.0, 0.144, 0.010); gold #ffcc4a = (1.0, 0.604, 0.069).
 */
const EMISSIVE_GLSL = /* glsl */ `
  {
    vec3 tdV = isOrthographic ? vec3(0.0, 0.0, 1.0) : normalize(vViewPosition);
    float tdNV = 1.0 - clamp(dot(normal, tdV), 0.0, 1.0);
    float tdF3 = tdNV * tdNV * tdNV;
    totalEmissiveRadiance += uTdRim.rgb * (uTdRim.a * tdF3);
    if (uTdStatus.x > 0.0) totalEmissiveRadiance += vec3(0.392, 0.686, 1.0) * (0.6 * uTdStatus.x * tdNV * tdNV);
    if (uTdStatus.y > 0.0) {
      float tdFeet = 1.0 - smoothstep(0.0, 0.6, clamp(vTdLocal.y / max(uTdH, 0.05), 0.0, 1.0));
      float tdFlick = 0.4 + 0.4 * tdNoise(vec3(vTdLocal.xz * 9.0 / max(uTdH, 0.05), uTdTime * 12.0));
      totalEmissiveRadiance += vec3(1.0, 0.144, 0.010) * (uTdStatus.y * tdFlick * (0.3 + 0.9 * tdFeet));
    }
    if (uTdStatus.z > 0.0) totalEmissiveRadiance += vec3(0.202, 1.0, 0.102) * (uTdStatus.z * (0.15 + 0.35 * tdPulse) * tdF3);
    if (uTdStatus.w > 0.0) totalEmissiveRadiance += vec3(1.0, 0.604, 0.069) * (uTdStatus.w * 0.9 * tdF3);
    if (uTdShiny.x > 0.0) totalEmissiveRadiance += tdShinyLight(normal, tdNV, tdF3, diffuseColor.rgb);
  }`;

/*
 * Flash, dissolve edge and the boss-death cracks on the final colour, just before tone mapping (after the map-haze
 * patch's opaque hook). Cracks: thin iso-lines of the value noise (|n − 0.5| small), revealed outward from the chest
 * as uTdCrack runs 0 → 1, glowing hot enough (×4) for the bloom pass.
 */
/* The rare's `_glow` slots (MeshBasicMaterial, the basic patch): they breathe with the shine's glow pulse, tinted to the rare. */
const SHINY_BASIC_GLSL = /* glsl */ `
  if (uTdShiny.x > 0.0) gl_FragColor.rgb = mix(gl_FragColor.rgb, gl_FragColor.rgb * (0.6 + 0.4 * uTdShinyA) * (1.0 + 1.6 * uTdShiny.z), uTdShiny.x);`;

const FINAL_GLSL = /* glsl */ `
  gl_FragColor.rgb = mix(gl_FragColor.rgb, vec3(1.0, 0.95, 0.9) * 2.2, uTdFlash * 0.75);
  gl_FragColor.rgb += uTdEdge * (tdEdgeGlow * 3.0);
  if (uTdCrack > 0.0) {
    vec3 tcP = vTdLocal / max(uTdH, 0.05);
    float tcLine = 1.0 - smoothstep(0.0, 0.03 + 0.03 * uTdCrack, abs(tdNoise(tcP * 5.0 + 3.1) - 0.5));
    float tcReach = 1.0 - smoothstep(uTdCrack * 0.95, uTdCrack * 0.95 + 0.12, length(tcP - vec3(0.0, 0.5, 0.0)));
    gl_FragColor.rgb += mix(uTdEdge, vec3(1.0, 0.92, 0.75), 0.5) * (tcLine * tcReach * 4.0);
  }`;

export type Uniforms = {
  uTdFlash: { value: number }; uTdRim: { value: THREE.Vector4 }; uTdStatus: { value: THREE.Vector4 }; uTdDissolve: { value: number }; uTdCrack: { value: number };
  uTdEdgeW: { value: number }; uTdEdge: { value: THREE.Color }; uTdH: { value: number }; uTdTime: { value: number };
  /** M36 rare shine: (strength, glitter cells per body height (0 = none), glow, hue drift) and the two sparkle colours. Zero strength for every other unit. */
  uTdShiny: { value: THREE.Vector4 }; uTdShinyA: { value: THREE.Color }; uTdShinyB: { value: THREE.Color };
};

function inject(src: string, anchor: string, code: string, after = true): string {
  if (!src.includes(anchor)) return src;                // an unexpected program: leave it untouched rather than break it
  return src.replace(anchor, after ? `${anchor}\n${code}` : `${code}\n${anchor}`);
}

export function patchShader(sh: THREE.WebGLProgramParametersWithUniforms, u: Uniforms, lit: boolean): void {
  Object.assign(sh.uniforms, u);
  sh.vertexShader = inject(sh.vertexShader, "#include <common>", "varying vec3 vTdLocal;");
  // after skinning: the vertex in the model's own space (the rigid rig's bone has placed the part), before projection
  sh.vertexShader = inject(sh.vertexShader, "#include <skinning_vertex>", "vTdLocal = transformed;");
  let fs = sh.fragmentShader;
  fs = inject(fs, "#include <common>", SHARED_GLSL + (lit ? SHINY_GLSL : ""));
  fs = inject(fs, "#include <clipping_planes_fragment>", DISSOLVE_GLSL);
  if (lit) {
    fs = inject(fs, "#include <color_fragment>", STATUS_ALBEDO_GLSL);
    fs = inject(fs, "#include <emissivemap_fragment>", EMISSIVE_GLSL);
  }
  if (!lit) fs = inject(fs, "#include <tonemapping_fragment>", SHINY_BASIC_GLSL, false);
  fs = inject(fs, "#include <tonemapping_fragment>", FINAL_GLSL, false);
  sh.fragmentShader = fs;
}

/** Materials that carry `normal`, `vViewPosition` and `totalEmissiveRadiance` in their fragment shader. */
export function isLit(m: THREE.Material): boolean {
  const s = m as THREE.MeshStandardMaterial;
  return !!(s.isMeshStandardMaterial || (m as THREE.MeshToonMaterial).isMeshToonMaterial || (m as THREE.MeshLambertMaterial).isMeshLambertMaterial || (m as THREE.MeshPhongMaterial).isMeshPhongMaterial);
}
